import { ChangeEvent, ElementType, FocusEvent, forwardRef } from 'react';
import { InputProps } from '@chakra-ui/react';
import { trioUITheme } from '@/styles/theme';
import * as Styles from './InputText.styles';
import WarningMessage from '../WarningMessage';

export type InputSize = 'sm' | 'md';

export type CustomInputProps = Omit<InputProps, 'value'> & {
  label?: string;
  name?: string;
  value?: string;
  errorMessage?: string;
  onChangeHandler?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlurHandler?: (event: FocusEvent<HTMLInputElement>) => void;
  isLabelAboveField?: boolean;
  isLink?: boolean;
  readonly?: boolean;
  alertMessage?: string;
  size?: InputSize;
  leadingIcon?: ElementType;
};

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      errorMessage,
      label,
      name,
      value,
      onBlurHandler,
      onChangeHandler,
      isLabelAboveField,
      size = 'sm',
      isLink = false,
      readonly,
      alertMessage,
      leadingIcon: LeadingIcon,
      ...props
    },
    ref
  ) => {
    const borderColor = alertMessage ? '#FF991C' : trioUITheme.colors['neutral-40'];

    return (
      <Styles.InputBox
        className="input-wrapper"
        isLabelAboveField={isLabelAboveField}
        data-testid="input-text"
      >
        {label && (
          <Styles.InputLabel isLabelAboveField={isLabelAboveField} htmlFor={name}>
            {label}
          </Styles.InputLabel>
        )}
        <Styles.InputArea isLabelAboveField={isLabelAboveField}>
          <Styles.Input
            ref={ref}
            boxShadow="none !important"
            fontSize="14px"
            name={name}
            id={name}
            w="100%"
            height="unset"
            lineHeight="21px"
            paddingLeft={`${LeadingIcon ? 28 : 12}px`}
            readOnly={readonly}
            borderColor={readonly ? 'transparent !important' : borderColor}
            focusBorderColor={trioUITheme.colors['secondary-60']}
            color={isLink ? trioUITheme.colors['primary-60'] : trioUITheme.colors['neutral-90']}
            isInvalid={Boolean(errorMessage)}
            errorBorderColor={trioUITheme.colors.warning}
            paddingRight="12px"
            paddingY={size === 'sm' ? '5.5px' : '10.5px'}
            data-testid={`input-text-${name}`}
            value={value}
            variant="outline"
            onChange={onChangeHandler}
            onBlur={onBlurHandler}
            borderRadius={4}
            backgroundColor={trioUITheme.colors['neutral-0']}
            {...props}
          />
          {LeadingIcon && <LeadingIcon />}
          <WarningMessage
            type={errorMessage ? 'error' : 'warning'}
            errorMessage={errorMessage || alertMessage}
            data-testid="error-message"
          />
        </Styles.InputArea>
      </Styles.InputBox>
    );
  }
);

export default CustomInput;
